import { inject, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Route,
  RouterModule,
  RouterStateSnapshot,
  Routes,
  UrlMatcher,
  UrlMatchResult,
  UrlSegment,
  UrlSegmentGroup
} from "@angular/router";
import { first } from "lodash-es";
import { setAccountNickName } from "src/app/core/account-nickname";
import { setDomainName } from "src/app/core/domain-name";
import { AuthGuard } from "src/app/guards/auth.guard";
import { ProcessInviteGuard } from "src/app/guards/process-invite.guard";
import { ProvideAuthenticatedUserGuard } from "src/app/guards/provide-authenticated-user.guard";
import { ProvideMemberAuthGuard } from "src/app//guards/provide-member-auth/provide-member-auth.guard";
import { ProvideMemberGuard } from "src/app/guards/provide-member.guard";
import { ProvideUserGuard } from "src/app/guards/provide-user.guard";
import { SessionGuard } from "src/app/guards/session.guard";
import { EnableThemeGuard } from "src/app/guards/enable-theme.guard";
import { EnableAbac } from "src/app/guards/enable-abac.guard";
import { ProvideMemberDisabledGuard } from "src/app/guards/provide-member-disabled.guard";
import { AuthenticateWithTokenGuard } from "src/app/guards/authenticate-with-token.guard";
import { ProvideBaseLocaleOverrideGuard } from "src/app/guards/provide-base-locale-override.guard";
import { SyncLocaleWithTenantAvailableGuard } from "src/app/guards/sync-locale-with-tenant-available.guard";
import { EnableServerEventsGuard } from "src/app/guards/enable-server-events.guard";
import { EnableIdleSessionGuard } from "src/app/guards/enable-idle-session.guard";
import { UpdateGuard } from "src/app/guards/update.guard";
import { ProvideConfigurationGuard } from "src/app/guards/configuration/provide-configuration.guard";
import { CookieAuthStorageGuard } from "src/app/guards/cookie-auth-storage.guard";
import { ProvideBoxGuard } from "src/app/guards/provide-box.guard";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { SessionService } from "src/app/services/session.service";
import { ProvideAvatarGuard } from "src/app/guards/provide-avatar.guard";
import { ProvideBreedGuard } from "src/app/guards/provide-breed.guard";
import { ProvideColorsGuard } from "src/app/guards/provide-colors.guard";
import { RoleGuard } from "src/app/guards/role.guard";
import { HideAppLoaderGuard } from "src/app/guards/hide-app-loader.guard";
import {
  getCurrentSoftwareProduct,
  SoftwareProduct
} from "src/app/support/product";
import { ProvideAdminMemberAuthGuard } from "src/app/guards/provide-member-auth/provide-admin-member-auth.guard";
import * as Sentry from "@sentry/angular";
import { ProvideRegionGuard } from "src/app/guards/provide-region.guard";
import { AllowExecutionSingleTabGuard } from "src/app/guards/allow-execution-single-tab.guard";
import { AppCustomPreloadingStrategy } from "src/app/routing/app-custom-pre-loading-strategy";
import { TenantRouterGuard } from "src/app/guards/tenant-router.guard";
import { FlagBasedPreloadingStrategy } from "src/app/routing/flag-based-preloading-strategy";
import { OnboardingGuard } from "src/app/guards/onboarding.guard";
import { SkipOnboardingGuard } from "src/app/guards/skip-onboarding.guard";
import { StatusPageGuard } from "src/app/guards/status-page.guard";
import { ConfigureAuthForDropPageGuard } from "src/app/guards/configure-auth-for-drop-page.guard";

const canActivateFnDeleteSessionByMember: CanActivateFn =
  async (): Promise<boolean> => {
    const member = inject(BootstrapService).getCurrentMember();
    const sessionService = inject(SessionService);

    if (member) {
      await sessionService.deleteByMemberId(member.id);
    }

    return true;
  };

const routes: Routes = [
  {
    path: "debug",
    loadChildren: () =>
      import("../modules/debug/debug.module").then((m) => m.DebugModule)
  },
  {
    path: "",
    canActivate: [AllowExecutionSingleTabGuard],
    children: [
      {
        path: "",
        canActivate: [ProvideBaseLocaleOverrideGuard],
        children: [
          {
            path: "locked",
            canActivate: [HideAppLoaderGuard],
            loadChildren: () =>
              import("../modules/locked/locked.module").then(
                (m) => m.LockedModule
              )
          },
          {
            path: "",
            children: [
              {
                path: "",
                canActivate: [SessionGuard],
                children: [
                  {
                    path: "unsubscribe",
                    canActivate: [HideAppLoaderGuard],
                    loadChildren: () =>
                      import("../modules/unsubscribe/unsubscribe.module").then(
                        (m) => m.UnsubscribeModule
                      )
                  },
                  {
                    path: "auth",
                    canActivate: [HideAppLoaderGuard],
                    loadChildren: () =>
                      import("../modules/auth/auth.module").then(
                        (m) => m.AuthModule
                      )
                  },
                  {
                    path: "not-found",
                    canActivate: [HideAppLoaderGuard],
                    loadChildren: () =>
                      import("../modules/not-found/not-found.module").then(
                        (m) => m.NotFoundModule
                      )
                  },
                  {
                    path: "maintenance",
                    canActivate: [HideAppLoaderGuard],
                    loadChildren: () =>
                      import("../modules/maintenance/maintenance.module").then(
                        (m) => m.MaintenanceModule
                      )
                  },
                  {
                    path: "onboarding",
                    canActivate: [SkipOnboardingGuard],
                    children: [
                      {
                        path: "",
                        canActivate: [HideAppLoaderGuard],
                        loadChildren: () =>
                          import("../modules/onboarding/onboarding.module").then(
                            (m) => m.OnboardingModule
                          )
                      }
                    ]
                  },
                  //https://pms.local.pet:4200/default/demo/organization/dashboard
                  {
                    path: "",
                    canActivate: [StatusPageGuard],
                    children: [
                      {
                        matcher: badUrlMatcher(),
                        children: [
                          {
                            path: ":domainCode/:accountCode/landing",
                            canActivate: [HideAppLoaderGuard],
                            loadChildren: () =>
                              import("../modules/landing/landing.module").then(
                                (m) => m.LandingModule
                              )
                          },
                          {
                            path: "",
                            canActivate: [ConfigureAuthForDropPageGuard],
                            children: [
                              {
                                path: "",
                                canActivate: [AuthenticateWithTokenGuard],
                                children: [
                                  {
                                    path: "",
                                    canActivate: [AuthGuard],
                                    children: [
                                      {
                                        path: "",
                                        canActivate: [ProvideUserGuard],
                                        children: [
                                          {
                                            path: "",
                                            canActivate: [EnableThemeGuard],
                                            children: [
                                              {
                                                path: "invite",
                                                canActivate: [
                                                  HideAppLoaderGuard
                                                ],
                                                loadChildren: () =>
                                                  import("../modules/invite/invite.module").then(
                                                    (m) => m.InviteModule
                                                  )
                                              },
                                              {
                                                path: "",
                                                canActivate: [
                                                  ProcessInviteGuard
                                                ],
                                                children: [
                                                  {
                                                    path: "",
                                                    canActivate: [
                                                      ProvideAuthenticatedUserGuard
                                                    ],
                                                    children: [
                                                      {
                                                        path: "router",
                                                        canActivate: [
                                                          HideAppLoaderGuard
                                                        ],
                                                        loadChildren: () =>
                                                          import("../modules/tenant-router/tenant-router.module").then(
                                                            (m) =>
                                                              m.TenantRouterModule
                                                          )
                                                      },
                                                      {
                                                        matcher:
                                                          onlyThisProduct(
                                                            SoftwareProduct.BOTMASTER
                                                          ),
                                                        canActivate: [],
                                                        children: [
                                                          {
                                                            path: "",
                                                            canActivate: [],
                                                            children: [
                                                              {
                                                                path: "",
                                                                canActivate: [],
                                                                children: [
                                                                  {
                                                                    path: "",
                                                                    canActivate:
                                                                      [
                                                                        HideAppLoaderGuard
                                                                      ],
                                                                    loadChildren:
                                                                      () =>
                                                                        import("../modules/bootstrap/bootstrap.module").then(
                                                                          (m) =>
                                                                            m.BootstrapModule
                                                                        )
                                                                  }
                                                                ]
                                                              }
                                                            ]
                                                          }
                                                        ]
                                                      },
                                                      {
                                                        matcher:
                                                          skipThisIfProducts([
                                                            SoftwareProduct.PLATFORM,
                                                            SoftwareProduct.BOTMASTER
                                                          ]),
                                                        canActivate: [
                                                          TenantRouterGuard
                                                        ],
                                                        children: [
                                                          {
                                                            path: "",
                                                            canActivate: [
                                                              ProvideAdminMemberAuthGuard
                                                            ],
                                                            children: [
                                                              {
                                                                path: "",
                                                                canActivate: [
                                                                  EnableAbac,
                                                                  ProvideMemberGuard,
                                                                  EnableServerEventsGuard
                                                                ],
                                                                children: [
                                                                  {
                                                                    path: "",
                                                                    canActivate:
                                                                      [
                                                                        HideAppLoaderGuard
                                                                      ],
                                                                    loadChildren:
                                                                      () =>
                                                                        import("../modules/bootstrap/bootstrap.module").then(
                                                                          (m) =>
                                                                            m.BootstrapModule
                                                                        )
                                                                  }
                                                                ]
                                                              }
                                                            ]
                                                          }
                                                        ]
                                                      },
                                                      {
                                                        matcher: urlMatcher(),
                                                        canActivate: [
                                                          "provideDomainAccountCodes"
                                                        ],
                                                        children: [
                                                          {
                                                            path: "",
                                                            canActivate: [
                                                              OnboardingGuard
                                                            ],
                                                            children: [
                                                              {
                                                                path: "",
                                                                canActivate: [
                                                                  ProvideMemberAuthGuard
                                                                ],
                                                                children: [
                                                                  {
                                                                    path: "",
                                                                    canActivate:
                                                                      [
                                                                        SyncLocaleWithTenantAvailableGuard,
                                                                        ProvideRegionGuard,
                                                                        ProvideMemberDisabledGuard
                                                                      ],
                                                                    children: [
                                                                      {
                                                                        path: "",
                                                                        canActivate:
                                                                          [
                                                                            EnableAbac,
                                                                            ProvideMemberGuard
                                                                          ],
                                                                        children:
                                                                          [
                                                                            {
                                                                              path: "",
                                                                              canActivate:
                                                                                [
                                                                                  EnableServerEventsGuard,
                                                                                  EnableIdleSessionGuard,
                                                                                  UpdateGuard,
                                                                                  ProvideConfigurationGuard,
                                                                                  CookieAuthStorageGuard,
                                                                                  RoleGuard,
                                                                                  ProvideBreedGuard,
                                                                                  ProvideColorsGuard,
                                                                                  ProvideAvatarGuard,
                                                                                  canActivateFnDeleteSessionByMember
                                                                                ],
                                                                              children:
                                                                                [
                                                                                  {
                                                                                    path: "",
                                                                                    canActivate:
                                                                                      [
                                                                                        /*
                                                                                  ProvideBoxGuard è stata spostata da sola perché nel caso in cui il permesso per l'accesso
                                                                                  alla rete locale non sia concesso è richiesta l'interazione con l'utente e viene messo
                                                                                  in attesa il caricamento dell'applicazione
                                                                                  */
                                                                                        ProvideBoxGuard
                                                                                      ],
                                                                                    children:
                                                                                      [
                                                                                        {
                                                                                          path: "",
                                                                                          loadChildren:
                                                                                            () =>
                                                                                              import("../modules/bootstrap/bootstrap.module").then(
                                                                                                (
                                                                                                  m
                                                                                                ) =>
                                                                                                  m.BootstrapModule
                                                                                              )
                                                                                        }
                                                                                      ]
                                                                                  }
                                                                                ],
                                                                              data: {
                                                                                preload: true
                                                                              }
                                                                            }
                                                                          ]
                                                                      }
                                                                    ]
                                                                  }
                                                                ]
                                                              }
                                                            ]
                                                          }
                                                        ]
                                                      }
                                                    ]
                                                  }
                                                ]
                                              }
                                            ]
                                          }
                                        ]
                                      }
                                    ]
                                  }
                                ]
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: "**",
            redirectTo: "not-found",
            pathMatch: "prefix"
          }
        ]
      }
    ]
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      enableViewTransitions: false,
      urlUpdateStrategy: "eager",
      preloadingStrategy: FlagBasedPreloadingStrategy
    })
  ],
  providers: [
    AppCustomPreloadingStrategy,
    FlagBasedPreloadingStrategy,
    {
      provide: "provideDomainAccountCodes",
      useValue: (
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
      ): boolean => {
        const { domainName, accountNickName } = route.params;

        setDomainName(domainName);
        setAccountNickName(accountNickName);

        Sentry.setTags({
          account: accountNickName,
          domain: domainName
        });

        return true;
      }
    }
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}

function badUrlMatcher(): UrlMatcher {
  return (segments: UrlSegment[], group: UrlSegmentGroup, route: Route) => {
    const currentProduct: SoftwareProduct = getCurrentSoftwareProduct();

    switch (currentProduct) {
      case SoftwareProduct.ADMIN: {
        return first(segments)?.path === "not-found" ? null : { consumed: [] };
      }
      default: {
        if (first(segments)?.path === "not-found") {
          return null;
        }

        return {
          consumed: []
        };
      }
    }
  };
}

function skipThisIfProduct(product: SoftwareProduct): UrlMatcher {
  return (
    segments: UrlSegment[],
    group: UrlSegmentGroup,
    route: Route
  ): UrlMatchResult | null => {
    const currentProduct: SoftwareProduct = getCurrentSoftwareProduct();

    switch (true) {
      case product === currentProduct: {
        return null;
      }
      default: {
        return {
          consumed: []
        };
      }
    }
  };
}

function skipThisIfProducts(products: SoftwareProduct[]): UrlMatcher {
  return (
    segments: UrlSegment[],
    group: UrlSegmentGroup,
    route: Route
  ): UrlMatchResult | null => {
    const currentProduct: SoftwareProduct = getCurrentSoftwareProduct();

    if (products.includes(currentProduct)) {
      return null;
    }

    return {
      consumed: []
    };
  };
}

function onlyThisProduct(product: SoftwareProduct): UrlMatcher {
  return (
    segments: UrlSegment[],
    group: UrlSegmentGroup,
    route: Route
  ): UrlMatchResult | null => {
    const currentProduct: SoftwareProduct = getCurrentSoftwareProduct();

    if (currentProduct === product) {
      return {
        consumed: []
      };
    }

    return null;
  };
}

function urlMatcher(): UrlMatcher {
  return (segments: UrlSegment[], group: UrlSegmentGroup, route: Route) => {
    const currentProduct: SoftwareProduct = getCurrentSoftwareProduct();

    switch (currentProduct) {
      case SoftwareProduct.ADMIN: {
        return {
          consumed: <UrlSegment[]>[]
        };
      }
      case SoftwareProduct.PLATFORM: {
        const [domainCodeOrReportingSegment] = segments;

        if (segments.length < 1) {
          return null;
        }

        return {
          consumed: [domainCodeOrReportingSegment],
          posParams: {
            domainName: new UrlSegment(domainCodeOrReportingSegment.path, {})
          }
        };
      }
      default: {
        const [domainCodeOrReportingSegment, accountNickNameSegment] = segments;

        if (segments.length < 2) {
          return null;
        }

        return {
          consumed: [domainCodeOrReportingSegment, accountNickNameSegment],
          posParams: {
            accountNickName: new UrlSegment(accountNickNameSegment.path, {}),
            domainName: new UrlSegment(domainCodeOrReportingSegment.path, {})
          }
        };
      }
    }
  };
}
